/**
 * File: TimerProvider.tsx
 * Path: apps/admin/src/shared/components/TimerProvider.tsx
 * Description: One running timer for the whole admin. Start it from a lead
 * or client's More menu; a small floating timer follows you between pages,
 * and survives a reload (kept on this device). Stopping asks what the time
 * was for and saves it as a time entry at the person's hourly rate.
 */

import React from "react";
import { Square, Timer, X } from "lucide-react";
import { useFloatingTimerController } from "../hooks/useFloatingTimerController";
import { TimerContext } from "../hooks/useTimerState";
import { useTimerProviderController } from "../hooks/useTimerProviderController";
import type { RunningTimer } from "../utils/timerUtils";
import { StopTimerDialog } from "./StopTimerDialog";

const FloatingTimer: React.FC<{ timer: RunningTimer; onStop: () => void; onDiscard: () => void }> = ({
  timer,
  onStop,
  onDiscard,
}) => {
  const { clock } = useFloatingTimerController(timer.startedAt);
  return (
    <div
      role="group"
      aria-label="Running timer"
      className="fixed bottom-24 right-gutter-sm z-40 flex items-center gap-stack-sm border border-primary/60 bg-card p-stack-sm shadow-lg sm:bottom-gutter-md sm:right-gutter-md"
    >
      <Timer className="h-4 w-4 text-primary" aria-hidden />
      <div className="min-w-0">
        <p className="max-w-40 truncate text-xs text-muted-foreground">{timer.record.name}</p>
        <p className="font-mono text-sm font-bold text-foreground" aria-live="off">
          {clock}
        </p>
      </div>
      <button
        type="button"
        onClick={onStop}
        className="inline-flex h-11 items-center gap-stack-xs bg-primary px-stack-sm text-sm font-bold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        <Square className="h-4 w-4" aria-hidden />
        Stop
      </button>
      <button
        type="button"
        onClick={onDiscard}
        aria-label="Discard the timer"
        className="inline-flex h-11 w-11 items-center justify-center text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
};

export const TimerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { api, running, stopping, beginStop, cancelStop, clear, discard } = useTimerProviderController();

  return (
    <TimerContext.Provider value={api}>
      {children}
      {running && <FloatingTimer timer={running} onStop={beginStop} onDiscard={discard} />}
      {running && stopping && <StopTimerDialog timer={running} onSaved={clear} onCancel={cancelStop} />}
    </TimerContext.Provider>
  );
};
